import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiErrorDetail } from "@/lib/api";
import { AlertCircle, ArrowRight, Mail, Lock, ShieldCheck } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { PasswordInput } from "@/components/PasswordInput";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";

function Logo() {
  return (
    <div className="flex flex-col items-center">
      <img src="/titanium-logo-light.png" alt="TITANIUM" data-testid="login-logo" className="h-24 w-auto object-contain sm:h-28" />
      <span className="mt-1 text-base font-extrabold tracking-[0.3em] text-[#111827]">TITANIUM <span className="text-[#8A641A]">i.OS</span></span>
    </div>
  );
}

const DARK_FIELD = "mt-1.5 h-12 border-[#CBD2DA] bg-white text-[15px] font-medium text-[#111827] placeholder:text-[#64748B] focus-visible:border-[#20466D] focus-visible:ring-[#20466D]/20";
const BTN = "h-12 w-full bg-[#111827] text-[15px] font-bold text-white hover:bg-[#1F2937]";

export default function Login() {
  const { login, loginMfa, register } = useAuth();
  const navigate = useNavigate();
  const [mode, setMode] = useState("login");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [reset, setReset] = useState(false);
  const [r, setR] = useState({ admin_email: "", admin_password: "", target_email: "", new_password: "" });
  // MFA (ROOT OWNER) step
  const [mfaStage, setMfaStage] = useState(false);
  const [preAuth, setPreAuth] = useState("");
  const [otp, setOtp] = useState("");
  const [useRecovery, setUseRecovery] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError(""); setLoading(true);
    try {
      if (mode === "login") {
        const res = await login(email, password);
        if (res?.mfa_required) {
          setPreAuth(res.pre_auth); setMfaStage(true); setLoading(false);
          return;
        }
      } else {
        await register(name, email, password);
      }
      navigate("/");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally { setLoading(false); }
  };

  const submitMfa = async (e) => {
    e.preventDefault();
    setError(""); setLoading(true);
    try {
      await loginMfa(preAuth, useRecovery ? "" : otp, useRecovery ? otp : "");
      navigate("/");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally { setLoading(false); }
  };

  const doReset = async (e) => {
    e.preventDefault();
    setError(""); setLoading(true);
    try {
      const { data } = await api.post("/auth/admin-reset-password", r);
      toast.success(`Password direset untuk ${data.email}. Silakan masuk sekarang.`);
      setReset(false); setEmail(r.target_email); setPassword(""); setR({ ...r, admin_password: "", target_email: "", new_password: "" });
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally { setLoading(false); }
  };

  return (
    <div data-testid="login-page" className="relative flex min-h-[100dvh] items-center justify-center overflow-hidden bg-[#F4F5F7] px-5 py-10">
      <div className="absolute inset-x-0 top-0 h-1.5 bg-[#B88929]" />
      <div className="relative z-10 w-full max-w-md">
        <div className="mb-6 flex flex-col items-center text-center">
          <Logo />
          <p className="mt-2 text-[13px] font-bold uppercase tracking-[0.24em] text-[#475569]">Body &amp; Paint Management System</p>
        </div>

        <div className="rounded-2xl border border-[#E2E5EA] bg-white p-6 shadow-[0_12px_40px_-20px_rgba(17,24,39,0.35)] sm:p-8">
          <h1 className="font-heading text-[26px] font-bold tracking-tight text-[#111827]">
            {mfaStage ? "Verifikasi Keamanan" : reset ? "Reset Password" : mode === "login" ? "Masuk" : "Buat Akun"}
          </h1>
          <p className="mt-1 text-[15px] font-medium text-[#475569]">
            {mfaStage ? "Masukkan kode dari aplikasi authenticator Anda" : reset ? "Owner/Admin mereset password user (tanpa email)" : mode === "login" ? "Akses workspace TITANIUM I.OS" : "Daftarkan akun staff baru"}
          </p>

          {error && (
            <div data-testid={mfaStage ? "mfa-error" : reset ? "reset-error" : "login-error"} className="mt-5 flex items-start gap-2 rounded-lg border border-[#E9A9A9] bg-[#FBEAEA] px-3 py-2.5 text-[15px] font-semibold text-[#B91C1C]">
              <AlertCircle size={16} className="mt-0.5 shrink-0" /> <span>{error}</span>
            </div>
          )}

          {mfaStage ? (
            <form onSubmit={submitMfa} className="mt-5 space-y-4">
              <div className="flex items-center gap-2 rounded-lg border border-[#9FB4CB] bg-[#E8EEF5] px-3 py-2.5 text-[14px] font-semibold text-[#20466D]">
                <ShieldCheck size={16} className="shrink-0" /> <span>Akun ROOT OWNER — verifikasi dua langkah wajib.</span>
              </div>
              <div>
                <Label className="text-[14px] font-semibold text-[#111827]">{useRecovery ? "Recovery code" : "Kode 6-digit"}</Label>
                <Input data-testid="mfa-otp-input" inputMode={useRecovery ? "text" : "numeric"} value={otp} onChange={(e) => setOtp(e.target.value)} required placeholder={useRecovery ? "recovery code" : "123456"} className={DARK_FIELD} autoFocus />
              </div>
              <Button type="submit" data-testid="mfa-submit-btn" disabled={loading} className={BTN}>{loading ? "Memverifikasi…" : "Verifikasi & Masuk"}</Button>
              <div className="flex items-center justify-between border-t border-[#E2E5EA] pt-4">
                <button type="button" data-testid="mfa-toggle-recovery" onClick={() => { setUseRecovery(!useRecovery); setOtp(""); setError(""); }} className="text-[15px] font-semibold text-[#475569] hover:text-[#111827]">{useRecovery ? "Gunakan kode authenticator" : "Gunakan recovery code"}</button>
                <button type="button" data-testid="mfa-cancel-btn" onClick={() => { setMfaStage(false); setOtp(""); setPreAuth(""); setError(""); }} className="text-[15px] font-bold text-[#20466D] hover:underline">Batal</button>
              </div>
            </form>
          ) : reset ? (
            <form onSubmit={doReset} className="mt-5 space-y-4">
              <div><Label className="text-[14px] font-semibold text-[#111827]">Email admin</Label><Input data-testid="reset-admin-email" type="email" value={r.admin_email} onChange={(e) => setR({ ...r, admin_email: e.target.value })} required className={DARK_FIELD} /></div>
              <div><Label className="text-[14px] font-semibold text-[#111827]">Password admin</Label><PasswordInput data-testid="reset-admin-password" value={r.admin_password} onChange={(e) => setR({ ...r, admin_password: e.target.value })} required className={DARK_FIELD} /></div>
              <div><Label className="text-[14px] font-semibold text-[#111827]">Email akun yang direset</Label><Input data-testid="reset-target-email" type="email" value={r.target_email} onChange={(e) => setR({ ...r, target_email: e.target.value })} required className={DARK_FIELD} /></div>
              <div><Label className="text-[14px] font-semibold text-[#111827]">Password baru (min 8)</Label><PasswordInput data-testid="reset-new-password" value={r.new_password} onChange={(e) => setR({ ...r, new_password: e.target.value })} required className={DARK_FIELD} /></div>
              <Button type="submit" data-testid="reset-submit-btn" disabled={loading} className={BTN}>{loading ? "Mohon tunggu…" : "Reset Password"}</Button>
              <button type="button" data-testid="reset-back-btn" onClick={() => { setReset(false); setError(""); }} className="w-full text-[15px] font-semibold text-[#475569] hover:text-[#111827]">Kembali ke halaman masuk</button>
            </form>
          ) : (
            <>
              <form onSubmit={submit} className="mt-5 space-y-4">
                {mode === "register" && (
                  <div>
                    <Label htmlFor="name" className="text-[14px] font-semibold text-[#111827]">Nama lengkap</Label>
                    <Input id="name" data-testid="register-name-input" value={name} onChange={(e) => setName(e.target.value)} required className={DARK_FIELD} />
                  </div>
                )}
                <div>
                  <Label htmlFor="email" className="text-[14px] font-semibold text-[#111827]">{mode === "login" ? "Email / User ID" : "Email"}</Label>
                  <div className="relative">
                    <Mail size={16} className="pointer-events-none absolute left-3 top-1/2 z-10 -translate-y-1/2 text-[#475569]" />
                    <Input id="email" type={mode === "login" ? "text" : "email"} data-testid="login-email-input" value={email} onChange={(e) => setEmail(e.target.value)} required placeholder={mode === "login" ? "email atau USR-001" : ""} className={`${DARK_FIELD} pl-9`} />
                  </div>
                </div>
                <div>
                  <Label htmlFor="password" className="text-[14px] font-semibold text-[#111827]">Password</Label>
                  <div className="relative">
                    <Lock size={16} className="pointer-events-none absolute left-3 top-1/2 z-10 -translate-y-1/2 text-[#475569]" />
                    <PasswordInput id="password" data-testid="login-password-input" value={password} onChange={(e) => setPassword(e.target.value)} required className={`${DARK_FIELD} pl-9`} />
                  </div>
                </div>
                <Button type="submit" data-testid="login-submit-btn" disabled={loading} className={`group ${BTN}`}>
                  {loading ? "Mohon tunggu…" : (
                    <span className="flex items-center justify-center gap-2">{mode === "login" ? "Masuk" : "Daftar"} <ArrowRight size={16} className="transition-transform group-hover:translate-x-0.5" /></span>
                  )}
                </Button>
              </form>

              <div className="mt-5 border-t border-[#E2E5EA] pt-4 text-right">
                <button data-testid="forgot-password-btn" onClick={() => toast.info("Lupa password? Hubungi Owner/HR untuk reset melalui Master Data. Anda wajib mengganti password saat login berikutnya.")} className="text-[15px] font-bold text-[#20466D] hover:underline">Lupa password?</button>
              </div>
            </>
          )}
        </div>
        <p className="mt-6 text-center text-[13px] font-semibold text-[#475569]">TITANIUM I.OS · Automotive Operations System</p>
      </div>
    </div>
  );
}
